/** Central metadata for public routes actually defined in App.js.
 * No new dependencies. Keep exact route capitalization: /worldFrameSeries etc.
 * The /allpaintings path is deliberately NOT listed because App.js only
 * defines /allpaintings/:category; see README for the two options.
 */
export const SITE_ORIGIN = "https://anjaanvivek.com";
export const DEFAULT_IMAGE =
  "https://res.cloudinary.com/wbwvb4sk/image/upload/v1786278466/elon1.jpg";

const HOME = {
  title: "Anjaan Vivek | Contemporary Artist & Pictorial Biography",
  description:
    "Explore contemporary paintings by Anjaan Vivek, creator of Pictorial Biography in a Single Frame and The World Frames. Discover original art, exhibitions and commissions.",
  image: DEFAULT_IMAGE,
  imageAlt: "The World Frame 1 – Elon Musk, artwork by Anjaan Vivek",
};

const STATIC_ROUTES = {
  "/": HOME,
  "/allprints": {
    title: "Limited Edition Fine Art Prints | Anjaan Vivek",
    description:
      "Explore fine art prints of original paintings by Anjaan Vivek. Discover the collection and available limited editions.",
  },
  "/worldFrameSeries": {
    title: "The World Frames | Pictorial Biography by Anjaan Vivek",
    description:
      "Discover The World Frames, a series by Anjaan Vivek portraying lives through their defining actions and events in a single uninterrupted canvas.",
  },
  "/about": {
    title: "About Anjaan Vivek | Indian Contemporary Artist",
    description:
      "Learn about Anjaan Vivek's painting practice, figurative and semi-abstract work, and his original Pictorial Biography in a Single Frame concept.",
  },
  "/exhibitions": {
    title: "Art Exhibitions | Anjaan Vivek",
    description:
      "Explore exhibitions and gallery presentations of Anjaan Vivek's paintings, including The World Frames and other original artworks.",
  },
  "/projects": {
    title: "Art Projects & Creative Practice | Anjaan Vivek",
    description:
      "Explore original art projects, commissions and creative collaborations by contemporary Indian artist Anjaan Vivek.",
  },
  "/customBiography": {
    title: "Custom Pictorial Biography Painting | Anjaan Vivek",
    description:
      "Discover commissioned pictorial biographies by Anjaan Vivek: a single painted composition telling a person's or organisation's story through actions and defining moments.",
  },
  "/contact": {
    title: "Contact Anjaan Vivek | Art & Commission Enquiries",
    description:
      "Contact Anjaan Vivek about original paintings, exhibitions, fine art prints and commissioned pictorial biography artworks.",
  },
};

const PRIVATE_SEGMENTS = new Set([
  "addpainting", "cart", "signup", "wishlist", "myoffers", "artistoffer",
  "myorders", "profile", "artistdashboard"
]);

function prettify(value) {
  let decoded = value || "";
  try { decoded = decodeURIComponent(decoded); } catch (_) { /* keep original */ }
  return decoded
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 70)
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function normalizePath(pathname = "/") {
  if (!pathname || pathname === "/") return "/";
  return pathname.replace(/\/+$/, "") || "/";
}

export function resolveRouteSEO(pathname) {
  const path = normalizePath(pathname);
  const shared = { ...HOME, noindex: false, type: "website" };

  if (STATIC_ROUTES[path]) return { ...shared, ...STATIC_ROUTES[path] };

  const [first, second] = path.split("/").filter(Boolean);
  const lowerFirst = (first || "").toLowerCase();
  if (PRIVATE_SEGMENTS.has(lowerFirst) || lowerFirst === "vipdossier" || path === "/preview/exhibition") {
    return {
      ...shared,
      title: "Private Page | Anjaan Vivek",
      description: "Private or account-specific page on Anjaan Vivek's website.",
      noindex: true,
      nofollow: true,
    };
  }

  if (path.startsWith("/allpaintings/") && second) {
    const category = prettify(second);
    return {
      ...shared,
      title: `${category} Paintings | Anjaan Vivek`,
      description: `Explore ${category.toLowerCase()} paintings and original artworks by contemporary Indian artist Anjaan Vivek.`,
    };
  }
  if (path.startsWith("/category/") && second) {
    const category = prettify(second);
    return {
      ...shared,
      title: `${category} Artworks | Anjaan Vivek`,
      description: `Discover ${category.toLowerCase()} artworks by Anjaan Vivek, including original contemporary paintings and related series.`,
    };
  }
  if (path.startsWith("/series/") && second) {
    const series = prettify(second);
    return {
      ...shared,
      title: `${series} Series | Anjaan Vivek`,
      description: `Explore ${series}, a painting series by contemporary artist Anjaan Vivek. Discover its artwork and artistic themes.`,
    };
  }
  if (path.startsWith("/paintingDetails/") && second) {
    return {
      ...shared,
      title: "Original Painting | Anjaan Vivek",
      description: "Explore the story, medium, and imagery of an original painting by Anjaan Vivek.",
      // Make metadata specific by calling usePageSEO(artworkMetadata)
      // inside PaintingDetails after the artwork has been loaded.
    };
  }
  if (path.startsWith("/originalDetails/") && second) {
    return {
      ...shared,
      title: "Original Artwork Details | Anjaan Vivek",
      description: "Discover the details and story behind an original artwork by Anjaan Vivek.",
    };
  }
  if (path.startsWith("/printDetails/") && second) {
    return {
      ...shared,
      title: "Fine Art Print Details | Anjaan Vivek",
      description: "View an artwork's fine art print edition and details by Anjaan Vivek.",
    };
  }

  // Unknown URL: don't let Google's index fill with empty React SPA pages.
  return {
    ...shared,
    title: "Page Not Found | Anjaan Vivek",
    description: "This page is not currently available on the Anjaan Vivek website.",
    noindex: true,
  };
}
